import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Flex,
    Select,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { getQuoteRevisions } from '../../api/revision-api';
import { getQuoteDiff, formatRevisionLabel, mapSnapshotToRows } from './utils';
import { QuoteTableView } from './QuoteTableView';
import { Modal } from 'newui/components/ui/Modal';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import type { RfxQuoteItem } from '../../types/quote.type';
import { size } from 'styled-system';

export interface CompareRevisionsModalProps {
    initialRevisionA?: number;
    initialRevisionB?: number;
    inviteId: number;
    isOpen: boolean;
    quoteId: number;
    quoteItems: RfxQuoteItem[];
    quoteName: string;
    onClose: () => void;
}

const CompareRevisionsModal: React.FC<CompareRevisionsModalProps> = ({
    isOpen,
    onClose,
    inviteId,
    quoteId,
    quoteName,
    quoteItems,
    initialRevisionA,
    initialRevisionB,
}) => {
    const [revisions, setRevisions] = useState<QuoteRevisionSnapshot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedA, setSelectedA] = useState<number | undefined>(initialRevisionA);
    const [selectedB, setSelectedB] = useState<number | undefined>(initialRevisionB);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const fetchRevisions = useCallback(async () => {
        if (!isOpen) {
            return;
        }
        setIsLoading(true);
        try {
            const data = await getQuoteRevisions({ inviteId: inviteId, quoteId });
            setRevisions(data);

            const maxRevision = Math.max(...data.map((r) => r.revision));
            const secondMax = data.length >= 2 ? maxRevision - 1 : 0;

            setSelectedA((prev) => prev ?? maxRevision);
            setSelectedB((prev) => prev ?? secondMax);
        } catch {
            // Error handled by global axios interceptor
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, inviteId, quoteId]);

    useEffect(() => {
        fetchRevisions();
    }, [fetchRevisions]);

    const revisionA = useMemo(
        () => revisions.find((r) => r.revision === selectedA),
        [revisions, selectedA]
    );

    const revisionB = useMemo(
        () => revisions.find((r) => r.revision === selectedB),
        [revisions, selectedB]
    );

    const diffKeys = useMemo(
        () =>
            revisionA && revisionB
                ? getQuoteDiff(revisionA, revisionB)
                : new Set<string>(),
        [revisionA, revisionB]
    );

    const rowsA = useMemo(
        () => (revisionA ? mapSnapshotToRows(revisionA, quoteItems) : []),
        [revisionA, quoteItems]
    );

    const rowsB = useMemo(
        () => (revisionB ? mapSnapshotToRows(revisionB, quoteItems) : []),
        [revisionB, quoteItems]
    );

    const hasMultipleRevisions = revisions.length >= 3;

    const handleSelectA = (value: number) => {
        if (value === selectedB) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedB(adjacent);
        }
        setSelectedA(value);
    };

    const handleSelectB = (value: number) => {
        if (value === selectedA) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedA(adjacent);
        }
        setSelectedB(value);
    };

    const labelA = hasMultipleRevisions
        ? formatRevisionLabel(selectedA ?? 0)
        : `${formatRevisionLabel((revisions[1]?.revision) ?? 1)} (Latest)`;

    const labelB = hasMultipleRevisions
        ? formatRevisionLabel(selectedB ?? 0)
        : 'Original';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            title="Compare Revisions"
            description={`for '${quoteName}'`}
            modalType="standard"
            iconType="info"
            closeOnEsc
            closeOnOverlayClick
            secondaryButton={{
                onClick: onClose,
                text: "Close",
                size: "sm",
                width: "full"
            }}
            wrapDescription
        >
            {isLoading ? (
                <Flex
                    justify="center"
                    align="center"
                    py={8}
                >
                    <Spinner size="lg" />
                </Flex>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {revisionA && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-700">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedA ?? ''}
                                        onChange={(e) => handleSelectA(Number(e.target.value))}
                                        data-testid="compare-select-a"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelA}
                                </p>
                                <QuoteTableView
                                    rows={rowsA}
                                    diffKeys={diffKeys}
                                    hoveredRowIndex={hoveredRowIndex}
                                    showUnitColumn
                                    showAllTotals
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}

                        {revisionB && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-500">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedB ?? ''}
                                        onChange={(e) => handleSelectB(Number(e.target.value))}
                                        data-testid="compare-select-b"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelB}
                                </p>
                                <QuoteTableView
                                    rows={rowsB}
                                    hoveredRowIndex={hoveredRowIndex}
                                    showUnitColumn
                                    showAllTotals
                                    muted
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};

export { CompareRevisionsModal };
